import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, filter, Observable, Subject, takeUntil } from 'rxjs';
import { DATE_FORMAT } from 'src/app/config/global.config';
import { AppState } from 'src/app/core/app.state';
import { DeleteCommissions, DeleteCommissionsSuccess, GetCommissions } from '../../../store/agents.actions';
import { ICommission } from '../../../models/commissions.models';
import { selectCommissions } from '../../../store/agents.selectors';
import { CommissionService } from '../../../services/commission.service';
import { CommissionComfirmationDialogComponent, CommissionDialogData } from '../commission-details/commission-comfirmation-dialog/commission-comfirmation-dialog.component';

@Component({
  selector: 'app-commissions-listing',
  templateUrl: './commissions-listing.component.html',
  styleUrls: ['./commissions-listing.component.scss'],
})
export class CommissionsListingComponent implements OnInit {
  commissions$: Observable<ICommission[]> = this.store.select(
    selectCommissions
  );
  commissions: ICommission[] = [];
  filteredCommissions: ICommission[] = [];
  selectedCommissions: number[] = [];
  searchInput = new FormControl('');

  activeSortKey: string | null = null;
  activeSortMode: 'asc' | 'desc' | null = null;

  DATE_FORMAT = DATE_FORMAT;

  destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private router: Router,
    public dialog: MatDialog,
    private translateService: TranslateService,
    private commissionService: CommissionService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.store.dispatch(GetCommissions());

    this.commissions$
      .pipe(
        filter((commissions) => commissions.length >= 0),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((commissions: ICommission[]) => {
        this.commissions = commissions;
        this.filteredCommissions = [...commissions];
      });

    this.actions$
      .pipe(ofType(DeleteCommissionsSuccess), takeUntil(this.destroy$))
      .subscribe(() => {
        this.selectedCommissions = [];
      });
  }

  getDescription(commission: ICommission) {
    const discountRules = this.translateService.instant('commission.listing.discount_rules');
    const volumeThresholds = this.translateService.instant('commission.listing.volume_thresholds');
    const discountAndVolumeThresholds = this.translateService.instant('commission.listing.discount_and_volume_thresholds');

    if (commission.discount_apply && commission.thresholds_apply) {
      return discountAndVolumeThresholds;
    } else if (commission.discount_apply) {
      return discountRules;
    } else if (commission.thresholds_apply) {
      return volumeThresholds;
    } else {
      return '';
    }
  }

  getCalculationType(commission: ICommission) {
    return commission.calculation_method ? this.commissionService.getCommissionCalculationTypeDesc(commission.calculation_method) : '';
  }

  search(event: any) {
    const key = event.target.value.toLowerCase();

    if (key != '') {
      this.filteredCommissions = this.commissions.filter((commission) =>
        commission.name.toLowerCase().includes(key)
      );
    } else {
      this.filteredCommissions = this.commissions;
    }
  }

  // Check if all commissions in all companies are selected
  isAllAgentsSelected(): boolean {
    const allCommissionIds = this.filteredCommissions.map((commission: any) => commission.id);
    return allCommissionIds.length
      ? allCommissionIds.every((id: any) => this.selectedCommissions.includes(id))
      : false;
  }

  // Check if at least one commission is selected but not all
  isPartialSelectedAll(): boolean {
    const allCommissionIds = this.filteredCommissions.map((commission: any) => commission.id);
    const selectedCount = allCommissionIds.filter((id: any) =>
      this.selectedCommissions.includes(id)
    ).length;
    return selectedCount > 0 && selectedCount < allCommissionIds.length;
  }

  toggleAllAgents(checked: boolean) {
    const allIds = this.filteredCommissions.map((l: any) => l.id!);
    this.selectedCommissions = checked ? [...allIds] : [];
  }

  toggleAgent(commissionId: number, checked: boolean) {
    if (checked) {
      this.selectedCommissions.push(commissionId);
    } else {
      this.selectedCommissions = this.selectedCommissions.filter((id) => id !== commissionId);
    }
  }

  sortByKey(key: string, sortDirection: 'asc' | 'desc'): void {
    if (key === this.activeSortKey && sortDirection === this.activeSortMode) return;
    if (!this.filteredCommissions || !Array.isArray(this.filteredCommissions)) return;

    const isEmpty = (v: any) => v === null || v === undefined || v === '';

    const isStrictDateString = (val: string): boolean => {
      // Matches YYYY-MM-DD, YYYY/MM/DD, with optional time (space or T separator)
      return /^\d{4}[-/]\d{2}[-/]\d{2}([ T]\d{2}:\d{2}:\d{2})?$/.test(val);
    }

    const compareValues = (a: any, b: any): number => {
      if (isEmpty(a) && isEmpty(b)) return 0;
      if (isEmpty(a)) return 1;
      if (isEmpty(b)) return -1;

     
      const isDateA = typeof a === 'string' && isStrictDateString(a);
      const isDateB = typeof b === 'string' && isStrictDateString(b);

      if (isDateA || isDateB) {
        console.log('Vals D', Date.parse(a), Date.parse(b), a, b)
        return new Date(a).getTime() - new Date(b).getTime();
      }

      if (typeof a === 'number' && typeof b === 'number') {
        console.log('Vals N', a, b)
        return a - b;
      }

      const valA = a.trim().toLowerCase();
      const valB = b.trim().toLowerCase();

      return String(valA).localeCompare(String(valB));
    };

    this.filteredCommissions = [...this.filteredCommissions].sort((a: any, b: any) => {
      let valA: any = a[key];
      let valB: any = b[key];

      

      const result = compareValues(valA, valB);
      return sortDirection === 'asc' ? result : -result;
    });

    this.activeSortKey = key;
    this.activeSortMode = sortDirection;
  }

  trackByAgentId(_index: number, commission: ICommission): string | number {
    return commission.id!;
  }

  goToDetails(commission: ICommission) {
    this.router.navigate(['/agents/commissions/', commission.id]);
  }

  deleteSelectedCommissions() {
    this.store.dispatch(
      DeleteCommissions({ ids: this.selectedCommissions })
    );
  }

  deleteConfirmation() {
    const data : CommissionDialogData = {
      dialogType: 'deleteAll',
      deleteCount: this.selectedCommissions.length
    };

    const dialogRef = this.dialog.open(CommissionComfirmationDialogComponent, {
      data
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.deleteSelectedCommissions();
      }
    });
  }

  newProfile() {
    this.router.navigate(['/agents/commissions/add']);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
